import React, { Fragment } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useIsMobile, useTrigger, useNavigate } from '@utils/hooks';

import { operationBlockActions, useStore } from '@modules';
import { CryptoCurrencyEnum } from '@modules/global/types';
import { VerificationStatusesEnum } from '@modules/user/types';

import Button from '@components/Button';
import { DataKeysEnum } from '@containers/OperationBlock/types';
import LoaderLayout from '@layouts/LoaderLayout';

import style from './Cryptocurrencies.scss';

const cn = bemCssModules(style, 'Cryptocurrencies');
const ti = getTestId('Cryptocurrencies');

export const Cryptocurrencies: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ global, user, cards }, dispatch] = useStore();
  const { currencies, loaders } = global;
  const { display_options } = currencies.config;
  const KYCStatusIsComplete = user.data?.checks.document === VerificationStatusesEnum.complete;
  const [showAll, setShowAll] = useTrigger(false);
  const isMobile = useIsMobile();
  const limit = isMobile ? 4 : 10;

  const onClick = (currency: CryptoCurrencyEnum) => {
    if (KYCStatusIsComplete) {
      const cardValue = cards.list[0]?.id || 'new_card';
      dispatch(operationBlockActions.setLeft({ group: DataKeysEnum.cards, value: cardValue }));
      dispatch(operationBlockActions.setRight({ group: DataKeysEnum.my_wallets, value: currency }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(CONST.nav.verifyIdentity);
    }
  };

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <LoaderLayout isLoading={loaders.getCurrencies}>
        <div className={cn('title', ['h2'])}>
          {KYCStatusIsComplete ? t('portfolio.buy_some_coins') : t('portfolio.cryptocurrencies')}
        </div>
        <div className={cn('table')}>
          {currencies.crypto.slice(0, showAll ? undefined : limit).map(currency => (
            <Fragment key={currency}>
              <div className={cn('name')}>{display_options[currency]?.fullname}</div>
              <Button className={cn('button')} onClick={() => onClick(currency)}>
                {t('public.buy_currency', { currency })}
              </Button>
              <div className={cn('divider')} />
            </Fragment>
          ))}
          <button className={cn('show-all', ['link'])} type="button" onClick={setShowAll}>
            {showAll ? t('wallets.show_less') : t('wallets.show_all')}
          </button>
        </div>
      </LoaderLayout>
    </div>
  );
};

export default Cryptocurrencies;
